import { z } from 'zod'
import { lenientNullable, timestampToDateNullable } from '@upadelmatch/core'
import { PLAYER_LEVEL_SOURCES } from '@upadelmatch/core/player-level'
import { PLAYER_POSITIONS } from './user'

// `players_public/{uid}` — the PROJECTION other players may read (spec/06).
// `users/{uid}` stays owner-only; Cloud Functions whitelist these fields into
// this doc (identity + level facts on user writes, trust counters on match
// results). Clients never write it.
export const PlayerPublicSchema = z.object({
  id: z.string(),
  name: z.string().nullable().default(null),
  photoURL: z.string().nullable().default(null),
  city: z.string().nullable().default(null),
  homeClubId: z.string().nullable().default(null),
  position: lenientNullable(z.enum(PLAYER_POSITIONS), 'playerPublic.position'),
  skillLevel: z.number().min(0).max(7).nullable().default(null),
  levelSource: lenientNullable(z.enum(PLAYER_LEVEL_SOURCES), 'playerPublic.levelSource'),
  levelReliability: z.number().min(0).max(100).nullable().default(null),
  /** Result-recorded matches the player showed up to (casual + competitive). */
  matchesPlayed: z.number().int().min(0).default(0),
  /** Times THIS player was flagged absent by a recorder/organizer (spec/06). */
  noShows: z.number().int().min(0).default(0),
  // ── The vouching record (spec/02 § Seats & identity) ───────────────────────
  // Separate from the pair above on purpose. Merged, "2 ausencias" could mean
  // this player flakes or that they invited two people who flaked — opposite
  // facts about whether to play with them, and a stranger reading one number
  // cannot tell which. A dirty signal is worse than a missing one: the missing
  // one gets ignored, the dirty one gets believed.
  /** Companions this player seated who turned up. */
  guestShows: z.number().int().min(0).default(0),
  /** Companions this player seated who did not. */
  guestNoShows: z.number().int().min(0).default(0),
  /**
   * Unclaimed placeholder seats held open right now, across live matches.
   * CF-maintained (`on_match_seats_written`) and eventually consistent — see
   * `vouchingAllowance`. Never trusted as exact; only compared to a ceiling.
   */
  openPlaceholders: z.number().int().min(0).default(0),
  memberSince: timestampToDateNullable,
  updatedAt: timestampToDateNullable,
})

export type PlayerPublicDoc = z.output<typeof PlayerPublicSchema>
